<template>
  <div class="Main-Inner" id="SessionViewer-MainInner">

    <div id="SessionViewerLayout" class="Main-Content">
      <Page 
        :title="!userViewer ? $t('SessionViewer.Session Viewer') : $t('SessionViewer.User Viewer')"
        size="fullWidth"
      >
        <!-- <LoadingLayout v-if="loading"/> -->
        <Layout v-if="noSession">
          <LayoutSection>
            <form v-on:keyup.enter="routeToSession">
              <Card>
                <CardSection>
                  <FormLayout>
                    <TextField
                      :label="$t('SessionViewer.Session Key')"
                      v-model.trim="sessionKey"
                      ref="sessionKey"
                    />
                    <Button type="primary" @click="routeToSession" fullWidth>{{$t('View')}}</Button>
                  </FormLayout> 
                </CardSection>
              </Card>
            </form>
          </LayoutSection>
        </Layout>
        <Layout v-else-if="userViewer">
          <LayoutSection>
            <SessionViewerUserHistory 
              :sessionFlow="sessionFlow" 
              :currency="accountCurrency" 
              :history="history" 
              :notifications="userNotifications"
              :dataLoading="userNotificationsLoading || loading"
              :viewSummary="viewUserSummary"
              :reverse.sync="reverseUserTimeline"
              :fromSession="fromSessionKey ? {
                sessionKey: fromSessionKey,
                date: fromSessionDate
              } : null"
              @back="cancelUserSummary"
              @refresh-session="updateSessionKey"
              @reverse-timeline="reverseUserTimeline = !reverseUserTimeline"
            />
          </LayoutSection>
        </Layout>
        <Layout v-else>
          <LayoutSection>
            <SessionViewerTimeline
              :sessionFlow="sessionFlow"
              :currency="domainCurrency || accountCurrency"
              :domainCurrency="domainCurrency"
              :history="history"
              :conversions="conversions"
              :accountCurrency="accountCurrency"
              :reverse.sync="reverseSessionTimeline"
              :loading="loading"
              :fromSession="fromSessionKey ? {
                sessionKey: fromSessionKey,
                date: fromSessionDate
              } : null"
              @update-session-key="updateSessionKey"
              @show-user-timeline="(showUserDetails) => showUserViewer(showUserDetails)"
              @expire-session="expireSession"
              @view-another-session="viewAnother"
              @refresh-session="refreshSession"
              @reverse-timeline="reverseSessionTimeline = !reverseSessionTimeline"
            />
          </LayoutSection>
        </Layout>
      </Page>
    </div>
  </div>

</template>

<script>

import SessionViewerTimeline from '../components/sessionViewer/SessionViewerTimeline'
import SessionViewerUserHistory from '../components/sessionViewer/SessionViewerUserHistory'
import SessionFlow from '../classes/SessionFlow.class'
import OffersUtil from '../classes/utils/OffersUtil'

export default {
  name: 'SessionViewerLayout',
  data() {
    return {
      loading: true,
      sessionKey: '',
      // isModal: false,
      session: null,
      sessionFlow: null,
      incentives: null,
      segments: null,
      widgets: null,
      orders: null,
      conversions: null,
      notificationDefs: null,
      userNotifications: null,
      userNotificationsLoading: true,
      history: null,
      accountTimezone: null,
      accountCurrency: null,
      domains: [],
      domainCurrency: null,
      userViewer: false,
      reverseSessionTimeline: false,
      reverseUserTimeline: false,
      viewUserSummary: false,
      noSession: false,
      fromSessionKey: null,
      fromSessionDate: null
    }
  },
  mounted() {
    this.$watch('$route.query', (to) => {
      if (to.sk && to.sk !== this.sessionKey) {
        this.sessionKey = to.sk
        this.noSession = false
        this.getSessionDetails()
      } else if (!to.sk) {
        this.noSession = true
        this.focusKey()
        this.viewAnother()
      }
      this.userViewer = !!(to.sk && to.user)
      this.fromSessionKey = to.sk && to.fromSessionKey
      this.fromSessionDate = to.sk && to.fromSessionDate
    })
    this.sessionKey = this.$route.query.sk
    if (!this.sessionKey) {
      this.loading = false
      this.userNotificationsLoading = false
      this.noSession = true
      this.focusKey()
      return
    }
    this.userViewer = !!this.$route.query.user
    this.fromSessionKey = this.$route.query.fromSessionKey
    this.fromSessionDate = this.$route.query.fromSessionDate
    this.getSessionDetails()
  },
  methods: {
    sessionExists(sk) {
      const curr = this.history && this.history.userSite && this.history.userSite.visits.find(h => h.sk === sk)
      return !!curr
    },
    route(sk, user = false) {
      const query = { sk }
      const newSessionExists = this.sessionExists(sk)
      if (newSessionExists) {
        const currSessionExists = this.sessionExists(this.session.sessionKey)
        if (!currSessionExists) {
          query.fromSessionKey = this.sessionKey
          query.fromSessionDate = this.convertHxDate(this.session.lastAccessedTime)
        } else {
          if (this.$route.query.fromSessionKey) {
            query.fromSessionKey = this.$route.query.fromSessionKey
          }
          if (this.$route.query.fromSessionDate) {
            query.fromSessionDate = this.$route.query.fromSessionDate
          }
        } 
      }
      if (user) {
        query.user = true
      }
      this.$router.push({ path: '/sessionViewer/', query})
        .catch(this.duplicateRouteCatch)
    },
    viewAnother() {
      this.sessionFlow = null
      this.$router.push({ path: '/sessionViewer/'})
        .catch(this.duplicateRouteCatch)
    },
    focusKey() {
      setTimeout(() => {
        this.$refs.sessionKey.focus()
      }, 100)
    },
    updateSessionKey(sessionKey) {
      this.loading = true
      this.userNotificationsLoading = true
      this.route(sessionKey)
      this.sessionKey = sessionKey
      this.userViewer = false
      this.viewUserSummary = false
      if (!this.sessionKey) {
        this.loading = false
        this.userNotificationsLoading = false
        return
      }
      this.getSessionDetails()
    },
    async expireSession() {
      const host = this.session.host
      if (!host) {
        this.$ui.alert({
          title: 'No Host'
        })
        return
      }
      
      const doExpire = await this.$ui.confirm({
        title: this.$t('SessionViewer.Expire Session?')
      })
      
      if (doExpire) {
        // expire the session
        this.api.expireSession(this.sessionKey, host)
        .then(() => {
          // inactivate
          this.session.isActive = false
          
          this.$ui.alert({
            title: this.$t('SessionViewer.Expiring Session'),
            content: this.$t('SessionViewer.expiringDesc')
          })

          // refresh ...
          setTimeout(this.refreshSession, 2000)
        })
      }
    },
    refreshSession() {
      this.getSessionDetails()
    },
    showOrder(order) {
      for (let i=0; i<this.sessionFlow.session.pages.length; i++) {
        const p = this.sessionFlow.session.pages[i]
        if (p.pageType === 'orderReset' && p.quoteId === order.orderNumber) {
          this.$refs.sessionViewerTimeline.showPageDetails(i)
          return
        }
      }
      // no order page
      this.$refs.sessionViewerTimeline.showOrderOrphan(order)
    },
    getSessionDetails() {
      NProgress.start()
      this.loading = true
      this.userNotificationsLoading = true
      this.api.getSessionDetails(this.sessionKey)
        .then((data) => {
          this.session = data.session
          this.incentives = OffersUtil.fillForFrontend(data.incentives || [])
          this.segments = data.segments || []
          this.widgets = data.widgets || []

          // not needed for session timeline (only for user viewer) so it can run asynchronously
          this.getUserNotifications()
          
          const ak = this.session.accountKey ? this.session.accountKey : this.session.pages[0].accountKey
          return Promise.all([
            this.api.getSalesOrdersBySessionKey(this.session.sessionKey),
            this.api.getConversionsBySessionKey(this.session.sessionKey),
            this.api.notifications.getNotifications(), // TODO: notifications needs to check for demo account
            this.api.getUserHistory(this.session.userSite.siteId, this.session.userKey, ak),
            this.api.getDomainsForSessionViewer()
          ])
        })
        .then((data) => {
          const [orders, conversions, notificationDefs, history, domains] = data
          this.orders = orders || []
          this.conversions = conversions || []
          this.notificationDefs = notificationDefs.notifications
          this.history = history
          this.accountTimezone = this.theUser.accountTimezone
          this.accountCurrency = this.theUser.accountCurrency
          this.domains = domains
          
          // find domain currency if it exists
          let domainDef = this.domains.find(d => d.domain === this.session.domain)
          if (domainDef && domainDef.defaultCurrencyOverride && domainDef.defaultCurrency) {
            this.domainCurrency = domainDef.defaultCurrency
          }

          this.sessionFlow = SessionFlow.init(
            this.session, 
            this.incentives, 
            this.segments, 
            this.widgets,
            this.orders, 
            this.notificationDefs, 
            this.accountTimezone)

          this.loading = false
        })
        .catch((e) => {
          NProgress.done()
          this.loading = false
          this.apiCatch(e)
          this.viewAnother()
        }) 
        .finally(() => {
          NProgress.done()
        })
  
    },
    convertHxDate(time) {
      if (time && time.__t) {
        const date = window.moment(time.__t * 1000).format('YYYY-MM-DD HH:mm:ss')
        return window.moment(date)
                .utcOffset(this.session.timeZone)
                .tz(this.theUser.accountTimezone)
                .format('YYYY-MM-DD HH:mm:ss')
      }
      return ''
    },
    format_ab(value) {
      if (value === null) {
        return this.$t('SessionViewer.Not Targeted')
      }
      return value ? 
        this.$t('SessionViewer.abControl', { pageNumber: `${this.session.abPageIndex + 1}` }) : 
        this.$t('SessionViewer.abTargeted', { pageNumber: `${this.session.abPageIndex + 1}`})
    },
    formatTimezone(offset) {
      const hours = parseInt(offset) / -60
      return hours > 0 ? `UTC+${hours}:00` : `UTC-${Math.abs(hours)}:00`
    },
    routeToSession() {
      if (this.sessionKey) {
        this.noSession = false
        this.route(this.sessionKey)
        this.getSessionDetails()
      }
    },
    showUserViewer(showUserDetails) {
      this.route(this.sessionKey, true)
      this.userViewer = true
      if (showUserDetails) {
        setTimeout(() => {
          this.viewUserSummary = true
        }, 100)
      }
    },
    cancelUserSummary() {
      this.route(this.sessionKey)
      this.userViewer = false
    },
    getUserNotifications() {
      this.api.getSessionKey()
        .then(sessionKey => {
          const url = 'https://api.fanplayr.com/session-viewer' +
            `?uk=${this.session.userKey}` +
            `&domain=${this.session.domain}` +
            `&pushInfo=${encodeURIComponent(JSON.stringify(this.session.pushInfo))}`
          return fetch(url, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'x-auth-key': sessionKey
            }
          })
          .then(res => res.json())
          .then(res => {
            return res.notifications
              .filter(item => !!item.definition)
              .map(item => {
                const def = item.definition
                return {
                  id: item.notificationLogId,
                  notificationId: item.notificationId,
                  name: def.name,
                  type: def.type,
                  status: item.status,
                  sentOn: this.convertDate(item.sentOn),
                  deliveredOn: item.viewedOn,
                  clicked: this.formatClickNotifications(item),
                  hint: `${this.$t('Log')}: ${item.notificationLogId}, ${this.$t('ID')}: ${item.notificationId}`
                }
              })
          })
          .then((res) => {
            this.userNotifications = res
            this.userNotificationsLoading = false
          })
          .catch(() => {
            this.userNotifications = []
            this.userNotificationsLoading = false
          })
        })
    },
    convertDate(date) {
      return date && window.moment.tz(date, 'GMT')
        .tz(this.theUser.accountTimezone)
        .format('YYYY-MM-DD HH:mm:ss')
    },
    formatClickNotifications(obj) {
      if (obj.defaultClicked) {
        return this.$t('SessionViewer.Yes (Default)')
      }
      if (obj.action1Clicked) {
        return this.$t('SessionViewer.Yes (Action 1)')
      }
      if (obj.action2Clicked) {
        return this.$t('SessionViewer.Yes (Action 2)')
      }
      return this.$t('No')
    },
  },
  components: {
    SessionViewerTimeline,
    SessionViewerUserHistory
  }
}

</script>

<style lang="scss">
#SessionViewer-MainInner {

  background: #363c4b !important;

}
</style>
